<template>
    <Transition
        name="nav-fade"
        @before-enter="show"
        @after-enter="lockScroll"
        @before-leave="unlockScroll"
        @after-leave="hide">
        <dialog
            v-show="globalNavOpened"
            ref="dialogEL"
            class="w-screen max-w-none min-h-screen bg-white backdrop:backdrop-filter backdrop:bg-opacity-0">
            <div class="fixed top-0 inset-x-0 z-1 bg-white flex justify-between items-center w-full h-16 md:h-32 px-4 md:px-20 md:pt-12 border-b-[0.5px] md:border-b-0 border-black-300">
                <slot name="logo" />
                <button
                    class="flex justify-center items-center md:w-20 md:h-20"
                    :aria-label="$globalLabels.close"
                    @click="globalNavOpened = false">
                    <Icon name="close" class="w-6 h-6 stroke-current" />
                </button>
            </div>

            <div class="px-4 md:px-20 pt-25 md:pt-44 pb-9 md:pb-20 grid md:grid-cols-[auto_1fr] gap-6 md:gap-15 lg:gap-30">
                <div class="md:w-40 lg:w-55">
                    <div class="text-heading-4 md:text-heading-3 lg:text-heading-2">{{ title }}</div>
                    <div class="mt-5">{{ description }}</div>
                </div>
                <div class="lg:mt-3">
                    <slot
                        :is-mobile-screen="isMobileScreen"
                        :open-region="openRegion"
                        :toggle-region="toggleRegion" />
                </div>
            </div>
        </dialog>
    </Transition>
</template>

<script lang="ts">
import Icon from '../base/Icon.vue';
import debounce from 'lodash/debounce';
import { mapWritableState } from 'pinia';
import { useNavigationStore } from '../../stores/navigation';
import { screens } from '../../plugins/breakpoints';

export default {
    components: { Icon },
    inject: ['$globalLabels', 'triggerOpenGlobalNav'],
    props: {
        title: { type: String, default: '' },
        description: { type: String, default: '' },
    },
    data() {
        return {
            isMobileScreen: window.innerWidth < screens.md,
            openRegion: null as string | null,
        };
    },
    computed: {
        ...mapWritableState(useNavigationStore, ['globalNavOpened']),
    },
    mounted(): void {
        window.addEventListener('resize', this.handleResize);
        document.addEventListener('keydown', this.handleEscape);

        // open the global nav, if the link contains the global nav hash
        this.loadDeepLink();
        // listen for changes of the hash
        window.addEventListener('hashchange', this.loadDeepLink);
    },
    beforeUnmount(): void {
        window.removeEventListener('resize', this.handleResize);
        document.removeEventListener('keydown', this.handleEscape);
        window.removeEventListener('hashchange', this.loadDeepLink);
    },
    methods: {
        show(): void {
            this.$refs.dialogEL.showModal();
        },
        hide(): void {
            this.$refs.dialogEL.close();
        },
        toggleRegion(regionId: string): void {
            if (!this.isMobileScreen) return;
            this.openRegion = this.openRegion === regionId ? null : regionId;
        },
        handleResize: debounce(function (): void {
            this.isMobileScreen = window.innerWidth < screens.md;
        }, 300),
        lockScroll(): void {
            document.body.style.overflow = 'hidden';
        },
        unlockScroll(): void {
            document.body.style.overflow = '';
        },
        handleEscape(event: KeyboardEvent): void {
            if (event.key === 'Escape') {
                this.globalNavOpened = false;
            }
        },
        loadDeepLink(): void {
            if (window.location.hash === '#global-nav') {
                // show global nav and remove the hash from the url
                this.triggerOpenGlobalNav();
                window.history.replaceState('', '', window.location.pathname + window.location.search);
            }
        },
    },
};
</script>
